import * as tus from 'tus-js-client';
import { io, Socket } from 'socket.io-client';
import { MessageReturnSchemas, type Message, type MessageReturnTypes } from 'shared/modelTypes';
import { useEffect, useState } from 'react';
import { generateUUID, truncate } from 'shared/util';
import { UserProfileSchema, type UserProfile } from 'shared/modelTypes/user';
import { redirect, useRouter } from '@tanstack/react-router';
import { useStore } from './store';
import type { ChatMessageRecord, FileUpload, TaskAnswerPhoto, TaskAnswerVideo, TaskState } from 'shared/modelTypes/client';
import { toast } from 'sonner';
import { clearWatchPosition, getAppVersion, watchPosition, type SemVer } from './lib';
import type { EventStatus } from 'shared/modelTypes/event';
import { networkLanguageLoader, setLanguage } from './translate';
import { OfflineSubmissionQueue } from './offlineSubmissionQueue';
import { setTag as sentrySetTag, setUser as sentrySetUser } from '@sentry/browser';
import { appConfig } from './appConfig';

// Note: for socket.io you need to provide the domain and path separately.
// In production we use the url https://domain.com/api
const url = new URL(import.meta.env.VITE_WEBSOCKET_URL);
const origin = url.origin;
const basePath = url.pathname.replace(/\/$/, '');
const path = `${basePath}/socket.io`;

export let socket: Socket | undefined;
const messageResolvers = new Map<string, (v: unknown) => void>();

export function initSocket() {
	if (socket) {
		socket.disconnect();
		messageResolvers.clear();
		console.log('disconnecting old socket');
	}

	// TODO: consider using jwt for authorization just like in the console application

	socket = io(origin, {
		transports: ['websocket'],
		path: path,
	});

	socket.on('connect', () => {
		console.log('@@@ socket connected');
		rejoinAfterGoingOffline();
	});
	socket.on('disconnect', () => {
		console.log('@@@ socket disconnected');
	});
	socket.on('error', (error) => {
		console.log('@@@ socket error', error);
	});
	socket.on('reconnect', () => {
		console.log('@@@ socket reconnected');
	});
	socket.on('reconnect_error', (error) => {
		console.log('@@@ socket reconnect error', error);
	});
	socket.on('reconnect_failed', () => {
		console.log('@@@ socket reconnect failed');
	});
	socket.on('connect_error', (error) => {
		console.log('@@@ socket connect error', error);
	});

	socket.onAny((eventName: string, payload: unknown) => {
		const resolver = messageResolvers.get(eventName);
		if (resolver) {
			resolver(payload);
			messageResolvers.delete(eventName);
		}
	});

	socket.on('event-chat-message', (payload: { message: ChatMessageRecord }) => {
		const state = useStore.getState();
		state.addChatMessage({
			...payload.message,
			channel: payload.message.channelId === null ? 'global' : 'game-master',
		});

		console.log(payload, 'MESSAGE');

		if (payload.message.teamId != state.teamId) {
			const sender =
				payload.message.teamId == null
					? 'Game Master'
					: state.teams.find((t) => t.id == payload.message.teamId)?.name || 'Unknown team';

			if (
				window.location.pathname != '/chat' ||
				(payload.message.channelId == null && state.activeChatTab == 'game-master') ||
				(payload.message.channelId != null && state.activeChatTab == 'global')
			) {
				playNotificationSound();
				toast.info(__`New message!`, {
					description: truncate(`${sender || 'Unknown sender'}: ${payload.message.message}`, 100),
				});
			}
		}
	});

	socket.on('event-task-submission', (payload: { submission: TaskState }) => {
		const state = useStore.getState();
		const prevSubmissionState = state.taskStates[payload.submission.taskId];

		const task = state.game?.rounds.flatMap((r) => r.tasks).find((t) => t.id == payload.submission.taskId);
		if (prevSubmissionState?.status == 'in-progress' && payload.submission?.status == 'done') {
			if (!['information', 'augmented-reality', 'fullscreen-video'].includes(task?.type ?? '') && !state.game?.hideScore) {
				//updated/completed/validated?
				const toastFunction = payload.submission.pointsGiven > 0 || task?.points == 0 ? toast.success : toast.error;
				toastFunction(`${task?.title ?? 'A task'} validated!`, {
					description: state.game?.hideScore ? undefined : payload.submission.completionResponse,
				});
			}
		}

		state.updateTaskSubmission(payload.submission);
	});

	socket.on('event-register-members', (payload: { teamId: string; members: { id: string; name: string }[]; ratId: string }) => {
		useStore.getState().setMembers(payload.members);
		useStore.getState().setRatId(payload.ratId);
	});

	socket.on('event-change-rat', (payload: { teamId: string; ratId: string }) => {
		useStore.getState().setRatId(payload.ratId);
	});

	socket.on('event-change-captain', (payload: { teamId: string; captainId: string }) => {
		const state = useStore.getState();
		if (state.teamId == payload.teamId) {
			state.setReadonlyState(state.deviceId !== payload.captainId);
		}
	});

	socket.on('event-set-hide-score', (payload: { hideScore: boolean }) => {
		useStore.getState().setHideScore(payload.hideScore);
	});

	socket.on('team-score-update', (payload: { teamId: string; score: number }) => {
		useStore.getState().updateTeamScore(payload.teamId, payload.score);
	});

	socket.on('event-change-round-unlock-received', (payload: { roundIds: string[]; teamId: string; unlocked: boolean }) => {
		const state = useStore.getState();
		if (payload.unlocked) {
			state.unlockRounds(payload.roundIds);
		} else {
			state.lockRounds(payload.roundIds);
		}
	});

	socket.on('event-change-round-unlock-global-received', (payload: { roundId: string; unlocked: boolean }) => {
		const state = useStore.getState();
		if (payload.unlocked) {
			state.unlockRounds([payload.roundId]);
		} else {
			state.lockRounds([payload.roundId]);
		}
	});

	socket.on('event-change-task-unlock-global-received', (payload: { taskId: string; unlocked: boolean }) => {
		const state = useStore.getState();
		if (payload.unlocked) {
			state.unlockTasks([payload.taskId]);
		} else {
			state.lockTasks([payload.taskId]);
		}
	});

	socket.on('team-avatar-update', (payload: { teamId: string; avatar: FileUpload | null }) => {
		useStore.getState().updateTeamAvatar(payload.teamId, payload.avatar);
	});

	socket.on('event-frozen', (payload: { frozen: boolean }) => {
		useStore.getState().setFreezeState(payload.frozen);
	});

	socket.on('event-status-changed', (payload: { status: EventStatus }) => {
		useStore.getState().setEventStatus(payload.status);
		if (payload.status == 'ended') {
			useStore.getState().leaveEvent();

			toast.info(__`Event ended.`);
			window.location.pathname = '/';
		}
	});

	socket.on('event-team-deleted', (payload: { teamId: string }) => {
		const teams = useStore.getState().teams;
		useStore.setState((state) => ({ ...state, teams: teams.filter((t) => t.id != payload.teamId) }));
		if (useStore.getState().teamId == payload.teamId) {
			useStore.getState().leaveEvent();

			toast.info(__`You got removed from the event.`);
			window.location.pathname = '/';
		}
	});

	socket.on('event-remove-device', (payload: { teamId: string; deviceId: string }) => {
		const teams = useStore.getState().teams;
		useStore.setState((state) => ({ ...state, teams: teams.filter((t) => t.id != payload.teamId) }));

		const state = useStore.getState();
		if (state.teamId == payload.teamId && state.deviceId == payload.deviceId) {
			state.leaveEvent();

			window.location.pathname = '/';
			toast.info(__`You got removed from the event.`);
		}
	});

	socket.on('event-team-joined', (payload: { teamId: string; name: string; score: number; rejoinCode: string }) => {
		useStore.getState().addTeam(payload.teamId, payload.name, payload.score, payload.rejoinCode);
	});

	socket.on('event-team-joined-existing', (payload: { teamId: string }) => {
		const state = useStore.getState();
		if (state.teamId == payload.teamId) {
			toast.error(__`Someone else joined your team`);
			if (!state.game?.spectateFunction) {
				state.leaveEvent();
				history.pushState(null, '/');
			} else {
				state.setReadonlyState(false);
			}
		}
	});

	socket.on('event-round-unlock-global-received', (payload: { roundId: string }) => {
		const state = useStore.getState();
		state.unlockRounds([payload.roundId]);
	});

	socket.on('event-round-unlock-received', (payload: { roundIds: string[] }) => {
		const state = useStore.getState();
		state.unlockRounds(payload.roundIds);
	});

	socket.on('event-change-task-unlock-received', (payload: { taskIds: string[]; teamId: string; unlocked: boolean }) => {
		const state = useStore.getState();
		if (payload.unlocked) {
			state.unlockTasks(payload.taskIds);
		} else {
			state.lockTasks(payload.taskIds);
		}
	});

	socket.connect();
}
initSocket();

export function emitMessage<T extends Message>(
	msg: T,
): MessageReturnTypes[T['kind']] extends null ? null : Promise<MessageReturnTypes[T['kind']]> {
	const __message_id = generateUUID();
	const returnSchema = MessageReturnSchemas[msg.kind];
	if (returnSchema) {
		// This message expects return data, so return a promise that resolves with the data
		const p = new Promise<unknown[T['kind']]>((resolve) => {
			messageResolvers.set(__message_id, resolve);
			socket?.emit('msg', { ...msg, __message_id });
		});

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return p as any;
	} else {
		// No return data expected, fire and forget
		socket?.emit('msg', { ...msg, __message_id, token: getToken() });
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return null as any;
	}
}

function getToken(): UserProfile | undefined {
	const token = localStorage.getItem('ubgames-jwt');
	if (!token) return undefined;

	// TODO: consider validating the token shape
	const rawToken = JSON.parse(token);
	const parsedToken = UserProfileSchema.safeParse(rawToken);
	if (!parsedToken.success) {
		console.error('Invalid token', parsedToken.error);
		return undefined;
	}

	return parsedToken.data;
}

export function useToken() {
	const [token, setTokenState] = useState(() => getToken());

	useEffect(() => {
		const handleStorage = () => {
			setTokenState(getToken());
		};

		window.addEventListener('token-storage', handleStorage);
		return () => window.removeEventListener('storage', handleStorage);
	}, []);

	function setToken(token: UserProfile | undefined) {
		localStorage.setItem('ubgames-jwt', token ? JSON.stringify(token) : '');
		setTokenState(token);
		window.dispatchEvent(new Event('token-storage')); // Ensure updates across tabs
	}

	return { token, setToken };
}

export const useOnline = () => {
	const [isOnline, setIsOnline] = useState(true);

	useEffect(() => {
		const handleOnline = () => setIsOnline(true);
		const handleOffline = () => setIsOnline(false);

		window.addEventListener('online', handleOnline);
		window.addEventListener('offline', handleOffline);

		// Set initial state
		setIsOnline(navigator.onLine);

		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, []);

	return isOnline;
};

export const useVersion = () => {
	const [value, setValue] = useState<undefined | SemVer>();

	useEffect(() => {
		getAppVersion().then(setValue);
	}, []);
	return value;
};

export let offlineSubmissionQueue: OfflineSubmissionQueue | undefined = undefined;

async function tusVideoUpload(opts: {
	//
	eventId: string;
	teamId: string;
	taskId: string;
	file: File;
}): Promise<{ success: false } | { success: true; videoId: string }> {
	try {
		const vidUrl = import.meta.env.VITE_WEBSOCKET_URL + '/start-task-video-upload';
		const vid = (await fetch(vidUrl, {
			method: 'post',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				eventId: opts.eventId,
				teamId: opts.teamId,
				taskId: opts.taskId,
				filename: opts.file.name,
			}),
		}).then((res) => res.json())) as
			| { success: false; message: string }
			| { success: true; libraryId: string; videoId: string; signature: string; expire: number };

		console.log('vid', vid);
		if (!vid.success) {
			toast(vid.message);
			return { success: false };
		}

		const uploadPromise = new Promise<{ success: false } | { success: true; videoId: string }>((resolve) => {
			const upload = new tus.Upload(opts.file, {
				endpoint: 'https://video.bunnycdn.com/tusupload',
				retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],
				headers: {
					AuthorizationSignature: vid.signature, // SHA256 signature (library_id + api_key + expiration_time + video_id)
					AuthorizationExpire: vid.expire.toString(), // Expiration time as in the signature,
					VideoId: vid.videoId, // The guid of a previously created video object through the Create Video API call
					LibraryId: vid.libraryId,
				},
				metadata: {
					filetype: opts.file.type,
					title: opts.file.name,
					// collection: 'collectionID',
				},
				onError: function (error) {
					console.error('upload error', error);
					resolve({ success: false });
				},
				onProgress: function (bytesUploaded, bytesTotal) {
					console.log('upload progress', bytesUploaded, bytesTotal);
				},
				onSuccess: function () {
					console.log('upload done!');
					resolve({ success: true, videoId: vid.videoId });
				},
			});

			// Check if there are any previous uploads to continue.
			upload.findPreviousUploads().then(function (previousUploads) {
				// Found previous uploads so we select the first one.
				if (previousUploads.length) {
					upload.resumeFromPreviousUpload(previousUploads[0]);
				}

				// Start the upload
				upload.start();
			});
		});

		return uploadPromise;
	} catch (error) {
		console.warn('TUS ERROR', error);
		return { success: false };
	}
}

export async function uploadTaskSubmission(
	submission: { eventId: string; teamId: string; submission: TaskState },
	file?: File,
	videoId?: string | null,
	allowQueue = true,
): Promise<{ success: boolean }> {
	try {
		const state = useStore.getState();
		const task = state.game?.rounds.flatMap((r) => r.tasks).find((t) => t.id === submission.submission.taskId);
		if (!task) {
			console.error('SubmitTaskSubmission: task not found');
			return { success: false };
		}

		const isVideoSubmission = submission.submission.answer?.kind === 'video';
		const url = import.meta.env.VITE_WEBSOCKET_URL + '/event-task-submission';

		const formData = new FormData();
		if (isVideoSubmission && !videoId) {
			if (!file) {
				console.error('no file for video submission');
				return { success: false };
			}

			const videoRes = await tusVideoUpload({
				eventId: submission.eventId,
				teamId: submission.teamId,
				taskId: submission.submission.taskId,
				file: file!,
			});

			if (!videoRes.success) {
				console.error('failed to upload video');

				if (allowQueue) {
					console.log('failed to upload video, adding to queue', submission.submission, file);
					offlineSubmissionQueue?.add(submission.submission, file);
				}

				return { success: false };
			} else {
				formData.set('videoId', videoRes.videoId);
				formData.set('videoMimeType', file.type);
				videoId = videoRes.videoId;
			}
		}

		if (file && !isVideoSubmission) {
			formData.append('file', file);
		}

		formData.append('submission', JSON.stringify(submission));
		formData.append('deviceId', state.deviceId as string);
		if (['video', 'photo'].includes(submission.submission.answer?.kind || '')) {
			const answer = submission.submission.answer as TaskAnswerPhoto | TaskAnswerVideo;
			if (answer?.message) formData.append('message', answer.message as string);
		}
		const res = (await fetch(url, {
			method: 'POST',
			body: formData,
		})
			.then(async (res) => {
				if (!res.ok) {
					throw new Error(`failed to fetch: ${res.status}`);
				}
				return res.json();
			})
			.catch((e: unknown) => {
				console.warn(e);
				// if (e instanceof TypeError) {
				// console.log('error message:', e.message);
				if (allowQueue) {
					console.log('failed to fetch, adding to queue', submission.submission, file);
					offlineSubmissionQueue?.add(submission.submission, videoId ? undefined : file, videoId);
				}
				// }
				return { success: false };
			})) as { success: boolean };

		if (res.success) {
			const autoValidate = task.type == 'automatic-validation';
			const showScore = !state.game?.hideScore;
			console.log('task submitted:', submission, task.type);
			let shouldShowToast =
				task.type == 'bingo' && submission.submission.answer?.kind == 'bingo' ? submission.submission.answer.bingo : true;

			if (autoValidate && showScore) shouldShowToast = false;
			if (task.type == 'fullscreen-video') shouldShowToast = false;
			if (task.type == 'information') shouldShowToast = false;
			if (state.game?.automaticNavigation == 'per-task') shouldShowToast = false;

			if (shouldShowToast) {
				toast.success(__`Task sent!`);
			}
		} else {
			let shouldShowToast =
				task.type == 'bingo' && submission.submission.answer?.kind == 'bingo' ? submission.submission.answer.bingo : true;

			if (state.game?.automaticNavigation == 'per-task') shouldShowToast = false;

			if (shouldShowToast) {
				toast.error(__`Failed to submit task`);
			}
		}

		return res;
	} catch (e) {
		console.error('Failed to upload task submission', e);
		return { success: false };
	}
}

export async function uploadAvatar(file: File | undefined, eventId: string, teamId: string) {
	const url = import.meta.env.VITE_WEBSOCKET_URL + '/event-avatar-upload';

	const formData = new FormData();
	if (file) {
		formData.append('file', file);
	}
	formData.append('eventId', eventId);
	formData.append('teamId', teamId);

	const res = (await fetch(url, {
		method: 'POST',
		body: formData,
	}).then((res) => res.json())) as { success: boolean };

	return res;
}

export function getFileExtension(filename: string | undefined): string | undefined {
	return filename?.split('.').pop();
}

export function getPhotoUrl(id: string, ext: string, teamId: string, eventId: string, library: 'console' | 'client' = 'client') {
	if (library == 'console') return `https://${import.meta.env.VITE_CDN}/${id}.${ext}`;
	return `https://${library == 'client' ? import.meta.env.VITE_CDN_GAME : import.meta.env.VITE_CDN}/${eventId}/${teamId}/${id}.${ext}`;
}

// @ts-expect-error untyped for now...
export const requireGameLoaded = async ({ location }) => {
	const { game } = useStore.getState();

	if (!game) throw redirect({ to: '/' });

	redirectBasedOnPlayState(location);
};

// @ts-expect-error untyped for now...
export const redirectIfGameLoaded = async ({ location }) => {
	const { game } = useStore.getState();

	if (!game) return;

	redirectBasedOnPlayState(location);

	throw redirect({ to: '/rounds' });
};

export function redirectBasedOnPlayState(location: Location) {
	const { playState } = useStore.getState();

	if (playState === 'intro' && location.pathname !== '/intro') throw redirect({ to: '/intro' });
	if (playState === 'registering-members' && location.pathname !== '/register-members') throw redirect({ to: '/register-members' });
	if (playState === 'showing-rat' && location.pathname !== '/showing-rat') throw redirect({ to: '/showing-rat' });
}

// TODO: find a nicer way to do this, or open a PR if none exists.
/**
 * Navigate back to the previous page with a view transition
 * @param router - The router instance
 * @param transition - The transition type to use
 * @default 'slide-right'
 */
export const navigateBack = (
	router: ReturnType<typeof useRouter>,
	transition: 'slide-left' | 'slide-right' | 'slide-up' | 'slide-down' = 'slide-right',
) => {
	router.options.defaultViewTransition = { types: [transition] };
	router.history.back();
	setTimeout(() => {
		router.options.defaultViewTransition = false;
	}, 100);
};

function rejoinAfterGoingOffline() {
	const startingState = useStore.getState();
	if (startingState.eventId && startingState.teamId) {
		// rejoin the event
		console.log('rejoining event', startingState.eventId, startingState.teamId, startingState.deviceId);

		emitMessage({
			kind: 'event-rejoin',
			id: startingState.eventId,
			teamId: startingState.teamId,
			deviceId: startingState.deviceId as string,
		}).then(async (res) => {
			console.log('rejoining offline', res);
			if (res?.success) {
				sentrySetTag('event', startingState.eventId);
				sentrySetUser({
					id: startingState.teamId,
					username: startingState.teams.find((t) => t.id === startingState.teamId)?.name ?? 'unknown',
				});
				if (res.init?.game?.game?.language) await setLanguage(res.init?.game?.game?.language, networkLanguageLoader);
				useStore.getState().init(res.init!);
				offlineSubmissionQueue = new OfflineSubmissionQueue(res.init!.eventId, res.init!.teamId);
				offlineSubmissionQueue.trySubmitAll();

				useStore.getState().setReadonlyState(!res.isCaptain || false);
			} else {
				console.log('Failed to rejoin event');
				useStore.getState().leaveEvent();
				history.pushState(null, '/');
				toast.error(__`Event not active`);
			}
		});
	} else {
		console.log('no event id or team id, not rejoining');
	}
}

export function rejoinEvent({
	eventId,
	teamId,
	teamName,
	deviceId,
}: {
	eventId: string;
	teamId: string;
	teamName: string;
	deviceId: string;
}) {
	emitMessage({ kind: 'event-rejoin', id: eventId, teamId: teamId, deviceId }).then(async (res) => {
		console.log('rejoin secondary', res);
		if (res?.success) {
			sentrySetTag('event', eventId);
			sentrySetUser({
				id: teamId,
				username: teamName,
			});
			if (res.init?.game?.game?.language) await setLanguage(res.init?.game?.game?.language, networkLanguageLoader);
			useStore.getState().init(res.init!);
			const state = useStore.getState();

			offlineSubmissionQueue = new OfflineSubmissionQueue(res.init!.eventId, res.init!.teamId);
			offlineSubmissionQueue.trySubmitAll();

			if (state.game?.type == 'double-dealer' && state.game) {
				const team = state.teams.find((x) => x.id == teamId);
				if (team?.data.members && team?.data.members?.length > 1) {
					state.setPlayState('playing');
				} else {
					state.setPlayState('registering-members');
				}
			} else {
				state.setPlayState('playing');
			}
			history.pushState({}, '', '/rounds');
			state.setReadonlyState(state.game?.spectateFunction == true);
		} else {
			console.log('Failed to rejoin event');
			useStore.getState().leaveEvent();
			history.pushState({}, '', '/');
			toast.error(__`Event not active`);
		}
	});
}

type GeoWatchState = {
	// tracks wether we are currently watching GPS
	isWatching: boolean;
	// the watchId of the current watcher, may be null if not resolved yet
	watchId: number | null;
};

const gps: GeoWatchState = {
	isWatching: false,
	watchId: null,
};

export async function updateGpsRequired() {
	const state = useStore.getState();

	if (!appConfig.permissions.allowGps) return;

	const isReadonly = state.isReadonly;
	const hasGame = state.game != undefined;
	const mapVisible = state.game?.showMap == true;
	const roundsRequireGps = state.game?.rounds.some((round) => round.availability == 'gps') == true;
	const tasksRequireGps =
		state.game?.rounds.map((round) => round.tasks.some((task) => task.availability == 'gps')).some((x) => x) == true;
	const requiresGps = !isReadonly && hasGame && (mapVisible || roundsRequireGps || tasksRequireGps);

	const stopWatching = () => {
		window.removeEventListener('pagehide', stopWatching);

		if (gps.watchId) clearWatchPosition(gps.watchId);
		gps.watchId = null;
		gps.isWatching = false;

		const state = useStore.getState();
		state.updatePlayerLocation({
			...state.playerLocation,
			isTracking: false,
		});
	};

	window.addEventListener('pagehide', stopWatching);

	if (!requiresGps) {
		console.info('GPS is no longer required, stopping watchPosition');
		stopWatching();
		return;
	}

	if (gps.isWatching) {
		// already watching GPS, exit without watchPosition
		return;
	}

	gps.isWatching = true;
	console.info('starting to watch GPS');

	const watchIdOrError = await watchPosition((pos, err) => {
		if (err) {
			if (typeof err === 'object' && err.code === err.PERMISSION_DENIED) {
				// this is a fatal error, stop watching for position updates
				console.warn('GPS permission denied', err);
				stopWatching();
			} else {
				// these are recoverable errors, log a warning and keep the watcher running
				console.warn('GPS error', err);

				// TODO: consider setting tracking:false here depending on how often this error occurs and how long it takes to recover
			}
			return;
		}

		const state = useStore.getState();
		const lat = pos?.coords?.latitude ?? state.playerLocation.lat;
		const lng = pos?.coords?.longitude ?? state.playerLocation.lng;
		const heading = pos?.coords?.heading ?? state.playerLocation.heading;
		const timestamp = pos?.timestamp ?? state.playerLocation.timestamp;
		state.updatePlayerLocation({
			lat,
			lng,
			heading,
			isTracking: true,
			timestamp,
		});
	});

	// gps was stopped, don't update state
	if (!gps.isWatching) return;

	if (typeof watchIdOrError === 'number') {
		gps.watchId = watchIdOrError;
	} else {
		console.warn('Unable to get GPS location', watchIdOrError);
		stopWatching();
	}
}

function playNotificationSound() {
	const audio = new Audio('./sfx/notification-ping-3.mp3');

	if (!audio) {
		console.warn('No audio element found for notification sound');
	}

	audio.play().catch((err) => {
		console.warn('Notification sound blocked until user interacts with page:', err);
	});
}
